/**
 * Skafe automatic gift ("compre N, ganhe 1 brinde").
 *
 * Config: `window.SkafeGiftPromoConfigs`, one entry per gift rule, rendered by
 * `snippets/skafe-gift-promo.liquid` from Theme settings > "Brinde automático" and
 * "Brinde automático 2". Rules are independent: each one reconciles only its own gift,
 * and no gift line ever counts as an eligible product for any rule.
 * Docs: docs/commerce/GIFT_PROMO.md.
 *
 * This keeps the cart tidy; it is NOT the enforcement. A cart built by URL, the Cart
 * API or another browser reaches checkout without this script, so the Shopify
 * discount is what must refuse an unearned gift.
 *
 * Each gift is reconciled to a single target instead of reacting to clicks:
 *   want = active ? min(maxGifts, floor(eligible / minQty)) : 0
 * eligibleSkus ["*"] means every originally priced line that is not a gift.
 * Rule 2 also requires R$ 19,90 before discounts, excluding gifts and shipping.
 * `/cart/update.js` sets absolute quantities per line key, so repeating a step
 * (double click, two tabs) converges on the same cart. Reconciles are serialized
 * per tab and re-read the cart until it is stable.
 *
 * It also runs while the promo is off or over, to remove a gift left in a cart.
 */

(function (root) {
  'use strict';

  var SOURCE = 'skafe-gift-promo';
  // Brasília has had no DST since 2019, so a fixed offset is exact.
  var BRT = '-03:00';
  var MAX_PASSES = 4;

  function parseBrt(text) {
    var m = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})$/.exec(String(text || '').trim());
    return m ? Date.parse(m[1] + 'T' + m[2] + ':00' + BRT) : NaN;
  }

  // Fails closed: a set but unparsable bound turns the promo off.
  function isActive(cfg, nowMs) {
    if (!cfg || !cfg.active) return false;
    if (cfg.startsAt && !(parseBrt(cfg.startsAt) <= nowMs)) return false;
    // The end minute is inclusive: 23:59 runs until 00:00.
    if (cfg.endsAt && !(nowMs < parseBrt(cfg.endsAt) + 60000)) return false;
    return true;
  }

  function norm(sku) {
    return String(sku || '').trim().toLowerCase();
  }

  // giftVariantIds: every rule's gift, so gifts never qualify each other.
  function plan(cart, cfg, nowMs, giftVariantIds) {
    var skus = (cfg.eligibleSkus || []).map(norm);
    var anyPriced = skus.indexOf('*') !== -1;
    var gifts = giftVariantIds || [cfg.giftVariantId];
    var eligible = 0;
    var subtotal = 0;
    var have = 0;
    (cart.items || []).forEach(function (item) {
      if (item.variant_id === cfg.giftVariantId) have += item.quantity;
      else if (gifts.indexOf(item.variant_id) !== -1) return;
      else if (anyPriced ? item.original_price > 0 : skus.indexOf(norm(item.sku)) !== -1) {
        eligible += item.quantity;
        subtotal += (item.original_price || 0) * item.quantity;
      }
    });
    var want = isActive(cfg, nowMs) && (cfg.rule !== 2 || subtotal >= 1990) ? Math.min(cfg.maxGifts, Math.floor(eligible / cfg.minQty)) : 0;
    return { eligible: eligible, have: have, want: want, change: want !== have };
  }

  function giftUpdates(cart, variantId, quantity) {
    var updates = {};
    var lines = (cart.items || []).filter(function (item) { return item.variant_id === variantId; });
    // Keep discounted units first; a variant ID only updates one of Shopify's split lines.
    lines.sort(function (a, b) { return a.final_price - b.final_price; });
    lines.forEach(function (item, index) {
      var keep = index === lines.length - 1 ? quantity : Math.min(quantity, item.quantity);
      updates[item.key] = keep;
      quantity -= keep;
    });
    if (!lines.length) updates[variantId] = quantity;
    return updates;
  }

  // deps: { cfg, now(), getCart(), setGift(quantity, cart), giftVariantIds? }. Resolves true when the cart changed.
  function createReconciler(deps) {
    var inflight = null;
    var again = false;
    // A refused or capped add (sold out) must not be retried on every cart event.
    // A network blip is not a refusal: it only ends this pass.
    var addBlocked = false;

    async function settle() {
      var changed = false;
      for (var pass = 0; pass < MAX_PASSES; pass++) {
        var snapshot = await deps.getCart();
        var p = plan(snapshot, deps.cfg, deps.now(), deps.giftVariantIds);
        if (!p.change || (p.want > p.have && addBlocked)) break;
        try {
          var cart = await deps.setGift(p.want, snapshot);
          changed = true;
          // /cart/update.js can answer 200 and still cap the quantity of an unavailable gift.
          if (p.want > 0 && cart && plan(cart, deps.cfg, deps.now(), deps.giftVariantIds).have < p.want) {
            addBlocked = true;
            break;
          }
        } catch (e) {
          if (p.want > p.have && e && e.refused) addBlocked = true;
          break;
        }
      }
      return changed;
    }

    return function reconcile() {
      if (inflight) {
        again = true;
        return inflight;
      }
      inflight = (async function () {
        var changed = false;
        do {
          again = false;
          if (await settle()) changed = true;
        } while (again);
        return changed;
      })().finally(function () {
        inflight = null;
      });
      return inflight;
    };
  }

  var api = { parseBrt: parseBrt, isActive: isActive, plan: plan, createReconciler: createReconciler, giftUpdates: giftUpdates };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.document) return;

  var cfgs = root.SkafeGiftPromoConfigs || [];
  if (!cfgs.length || root.SkafeGiftPromo) return;
  var giftVariantIds = cfgs.map(function (c) { return c.giftVariantId; });

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      if (!res.ok) throw new Error('cart fetch failed');
      return res.json();
    });
  }

  // Dawn's remove/quantity buttons address a line by position, which shifts when the gift
  // line is added or removed. Block them until the sections are re-rendered.
  function lockRows(locked) {
    ['main-cart-items', 'CartDrawer-CartItems'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.classList.toggle('cart__items--disabled', locked);
    });
  }

  // One request per gift: a sold-out gift refuses only its own update, never the other's.
  function setGift(variantId, quantity, cart) {
    lockRows(true);
    var updates = giftUpdates(cart, variantId, quantity);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (res) {
      if (!res.ok) throw Object.assign(new Error('gift update refused'), { refused: res.status === 422 });
      return res.json();
    });
  }

  // Same sections the Dawn cart re-renders after a quantity change.
  function refreshSurfaces() {
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    var targets = [{ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
    if (items && footer) {
      targets.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
      targets.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    }
    var url = root.location.pathname + '?sections=' + targets.map(function (t) { return t.section; }).join(',');
    return fetch(url)
      .then(function (res) { return res.json(); })
      .then(function (sections) {
        targets.forEach(function (t) {
          var host = document.getElementById(t.id);
          var html = sections[t.section];
          if (!host || !html) return;
          var target = host.querySelector(t.selector) || host;
          var source = new DOMParser().parseFromString(html, 'text/html').querySelector(t.selector);
          if (source) target.innerHTML = source.innerHTML;
        });
      })
      .catch(function () {});
  }

  var reconcilers = cfgs.map(function (cfg) {
    return createReconciler({
      cfg: cfg,
      now: Date.now,
      getCart: getCart,
      giftVariantIds: giftVariantIds,
      setGift: function (quantity, cart) { return setGift(cfg.giftVariantId, quantity, cart); },
    });
  });

  // Rules settle one after another, so each one reads the cart the previous one left.
  async function reconcile() {
    var changed = false;
    for (var i = 0; i < reconcilers.length; i++) {
      if (await reconcilers[i]()) changed = true;
    }
    return changed;
  }

  // Dawn only sets these when its own request empties the cart; ours can empty it afterwards.
  function syncEmptyState(cart) {
    var empty = cart.item_count === 0;
    ['cart-drawer', 'cart-drawer-items', 'cart-items', 'main-cart-footer'].forEach(function (name) {
      var el = document.querySelector(name) || document.getElementById(name);
      if (el) el.classList.toggle('is-empty', empty);
    });
  }

  function run() {
    return reconcile()
      .then(function (changed) {
        if (!changed) return;
        return getCart().then(function (cart) {
          syncEmptyState(cart);
          // The drawer re-renders itself on this event.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
          }
          return refreshSurfaces();
        });
      })
      .catch(function () {})
      .then(function () {
        lockRows(false);
      });
  }

  // Without a cart cookie there is nothing to reconcile on passive triggers.
  function passive() {
    if (document.cookie.indexOf('cart=') !== -1) run();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === SOURCE) return;
      run();
    });
  }
  root.addEventListener('pageshow', function (event) {
    if (event.persisted) passive();
  });
  // Another tab may have changed the cart.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') passive();
  });

  root.SkafeGiftPromo = {
    reconcile: run,
    plan: function (cart) {
      return cfgs.map(function (cfg) { return plan(cart, cfg, Date.now(), giftVariantIds); });
    },
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', passive);
  else passive();
})(typeof window !== 'undefined' ? window : globalThis);
